import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, SearchSchemaInput } from '@tanstack/react-router'
import { DataTable } from "@/components/features/orders/data-table"
import { columns } from "@/components/features/orders/columns"
import { DatePicker } from "@/components/ui/datepicker"
import { useEffect, useState } from 'react'
import { useStore } from '@/store'
import { useShallow } from 'zustand/shallow'
import { Loader2, RefreshCw, MapPinned, List, Plus, LayoutList, Download } from 'lucide-react'
import { Order } from '@/lib/interfaces'
import { Api, apiHost, featureEnvironmentStatus, getLspName, is_bulk_order_enabled_account, is_shikhar_account } from '@/lib/utils'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { Pagination } from '@/components/ui/pagination'
import { useNavigate } from '@tanstack/react-router';
import MapView from "@/components/features/orders/map-view"
import Tooltip from '@/components/ui/tooltip'
import Select from '@/components/ui/select'
import { AddSlotOrderDialog } from '@/components/features/orders/add-slot-order-dialog'
import { toast } from 'sonner'

export const Route = createFileRoute('/_auth/orders/')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown> & SearchSchemaInput): { date?: string } => {
    return search.date ? { date: search.date as string } : {}
  }
})

function RouteComponent() {
  const { token, unfulfil_enabled, role, accountId, setMainPage, isRetail, loggedIn } = useStore(useShallow(state => ({
    token: state.token,
    unfulfil_enabled: state.selectedAccount.unfulfil_enabled,
    role: state.selectedAccount.role,
    accountId: state.selectedAccount.id,
    setMainPage: state.setPage,
    isRetail: state.selectedAccount.is_retail,
    loggedIn: state.loggedIn
  })))
  const isBulkOrderEnabled = is_bulk_order_enabled_account(accountId)

  const [outlets, setOutlets] = useState<string[]>([])
  const [status, setStatus] = useState<string[]>([])
  const [lsp, setLsp] = useState<string[]>([])
  const [page, setPage] = useState(1)
  const [view, setView] = useState<'list' | 'map'>('list')
  const [markerType, setMarkerType] = useState<'pickup' | 'drop' | 'all'>('all')
  const isShikhar = is_shikhar_account(accountId)
  const showAddSlotOrder = featureEnvironmentStatus('slotOrder') && accountId !== 'mp2_preprod'

  const navigate = useNavigate({ from: Route.fullPath })

  useEffect(() => {
    if (isShikhar && loggedIn) {
      navigate({ to: '/shikhar/neworder' })
    }
  }, [isShikhar, loggedIn, navigate])

  const today = dayjs().format('YYYY-MM-DD')
  const { date: dateParam } = Route.useSearch()
  const date = dateParam || today
  const recordsPerPage = 100

  const getOrders = useQuery({
    queryKey: ['orders', date, token],
    queryFn: () => Api<Order[]>('/webui/orders_detailed_v2', "POST", { date: dayjs(date).format('YYYY-MM-DD') }, { token }),
    staleTime: 60 * 1000,
    refetchInterval: 90 * 1000,
    refetchOnWindowFocus: true,
    enabled: loggedIn
  })

  const filteredOrders = (getOrders.data || []).filter(order => {
    return (outlets.length === 0 || outlets.includes(order.pickupName)) &&
      (status.length === 0 || status.includes(order.orderState)) &&
      (lsp.length === 0 || lsp.includes(getLspName(order.bppId || order['f.bppId'], order.providerId)))
  })

  useEffect(() => {
    setMainPage('Orders')
  }, [])

  useEffect(() => {
    setOutlets([])
    setStatus([])
    setLsp([])
    setPage(1)
  }, [token, date])

  useEffect(() => {
    if (isShikhar) {
      setOutlets([])
    }
  }, [isShikhar])

  const queryClient = useQueryClient()

  const [isDownloadingLabels, setIsDownloadingLabels] = useState(false)

  const handleDownloadLabels = async () => {
    setIsDownloadingLabels(true)
    try {
      const resp = await fetch(`${apiHost}/webui/orders/pickup_proofs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Token': token },
        body: JSON.stringify({ date: dayjs(date).format('YYYY-MM-DD') }),
      })
      const data = await resp.json()
      const ordersWithProof = (data.orders || []).filter((o: { pickupProof: string | null }) => o.pickupProof)
      if (ordersWithProof.length === 0) {
        toast.info('No pickup labels available')
        return
      }
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()
      await Promise.all(ordersWithProof.map(async (order: { clientOrderId: string; pickupProof: string }) => {
        const imgResp = await fetch(order.pickupProof)
        const blob = await imgResp.blob()
        const ext = order.pickupProof.split('.').pop()?.split('?')[0] || 'png'
        zip.file(`${order.clientOrderId}_pickup.${ext}`, blob)
      }))
      const zipBlob = await zip.generateAsync({ type: 'blob' })
      const url = URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'pickup_labels.zip'
      a.click()
      URL.revokeObjectURL(url)
      toast.success(`Downloaded ${ordersWithProof.length} label(s)`)
    } catch {
      toast.error('Failed to download labels')
    } finally {
      setIsDownloadingLabels(false)
    }
  }

  return (
    <div className='fixed top-14 left-0 w-full h-full p-2 bottom-0'>
      <div className='flex items-start justify-between'>
        <div className='flex items-center mb-4 gap-5'>
          <DatePicker date={dayjs(date).toDate() || new Date()} onChange={date => {
            const selected = dayjs(date).format('YYYY-MM-DD')
            navigate({
              search: () => selected === today ? {} : { date: selected },
            })
          }} />
          {getOrders.isFetching ? <div className='flex items-center gap-3'>
            <Loader2 className='w-6 h-6 animate-spin' />
            <p className='hidden lg:block'>Refreshing...</p>
          </div> : null}
        </div>
        <div className='items-center gap-2 flex'>
          <div className='hidden mr-3 sm:flex items-center gap-2'>
            {view === 'map' ? <Select options={[{ label: 'Pickup', value: 'pickup' }, { label: 'Drop', value: 'drop' }, { label: 'All', value: 'all' }]} value={markerType} onChange={val => setMarkerType(val as 'pickup' | 'drop' | 'all')} /> : null}
            <Tooltip title={`View as ${view === 'list' ? 'Map' : 'List'}`}>
              <div className='p-1 border-2 cursor-pointer rounded-full border-gray-600'>
                {view === 'list' ? <MapPinned size={20} strokeWidth={2} onClick={() => setView('map')} /> :
                  <List size={20} onClick={() => setView('list')} />}
              </div>
            </Tooltip>
          </div>
          {isRetail ? <div className='xl:hidden'>
            <div className='flex items-center gap-2'>
              {isBulkOrderEnabled ? <Button variant={'outline'} loading={isDownloadingLabels} onClick={handleDownloadLabels}>
                <Download />
              </Button> : null}
              {isBulkOrderEnabled ? <Link to='/orders/bulk_order'>
                <Button variant={'blue'}>
                  <LayoutList />
                </Button>
              </Link> : null}
              <Link to='/orders/add_order'>
                <Button variant={'blue'}>
                  <Plus />
                </Button>
              </Link>
              {showAddSlotOrder ? <AddSlotOrderDialog token={token} onSuccess={() => getOrders.refetch()} compact /> : null}
            </div>
          </div> : null}
          <Button onClick={() => {
            getOrders.refetch()
          }}>
            <RefreshCw />
            <p className='hidden sm:block'>Refresh</p>
          </Button>
        </div>
      </div>
      <div className='flex items-center justify-between'>
        <div className='flex lg:flex-row lg:items-center lg:gap-4 pb-4 flex-col items-start gap-2 flex-1 min-w-0'>
          {!isShikhar ? (
            <>
              <div className='lg:hidden'>
                <MultiSelectFilter
                  title="Outlets"
                  options={(getOrders.data ?? []).map(order => order.pickupName).filter((state, index, self) => self.indexOf(state) === index)}
                  selectedOptions={outlets}
                  onSelect={(val) => {
                    outlets.includes(val) ? setOutlets(outlets.filter(f => f !== val)) :
                      setOutlets([...outlets, val])
                    setPage(1)
                  }} size='md' searchOptions />
              </div>
              <div className='hidden lg:block'>
                <MultiSelectFilter
                  title="Outlets"
                  options={(getOrders.data ?? []).map(order => order.pickupName).filter((state, index, self) => self.indexOf(state) === index)}
                  selectedOptions={outlets}
                  onSelect={(val) => {
                    outlets.includes(val) ? setOutlets(outlets.filter(f => f !== val)) :
                      setOutlets([...outlets, val])
                    setPage(1)
                  }} size={isBulkOrderEnabled ? 'sm' : 'lg'} searchOptions />
              </div>
            </>
          ) : null}
          <div className='lg:hidden'>
            <MultiSelectFilter
              title="Status"
              options={(getOrders.data ?? []).map(order => order.orderState).filter((state, index, self) => self.indexOf(state) === index)}
              selectedOptions={status}
              onSelect={(val) => {
                status.includes(val) ? setStatus(status.filter(f => f !== val)) :
                  setStatus([...status, val])
                setPage(1)
              }} size='md' />
          </div>
          <div className='hidden lg:block'>
            <MultiSelectFilter
              title="Status"
              options={(getOrders.data ?? []).map(order => order.orderState).filter((state, index, self) => self.indexOf(state) === index)}
              selectedOptions={status}
              onSelect={(val) => {
                status.includes(val) ? setStatus(status.filter(f => f !== val)) :
                  setStatus([...status, val])
                setPage(1)
              }} size={isBulkOrderEnabled ? 'sm' : 'md'} />
          </div>
          <div className='lg:hidden'>
            <MultiSelectFilter
              title="LSP"
              options={(getOrders.data ?? []).filter(order => order.bppId || order['f.bppId']).map(order => getLspName(order.bppId || order['f.bppId'], order.providerId)).filter((state, index, self) => self.indexOf(state) === index).sort()}
              selectedOptions={lsp}
              onSelect={(val) => {
                lsp.includes(val) ? setLsp(lsp.filter(f => f !== val)) :
                  setLsp([...lsp, val])
                setPage(1)
              }} size='md' />
          </div>
          <div className='hidden lg:block'>
            <MultiSelectFilter
              title="LSP"
              options={(getOrders.data ?? []).filter(order => order.bppId || order['f.bppId']).map(order => getLspName(order.bppId || order['f.bppId'], order.providerId)).filter((state, index, self) => self.indexOf(state) === index).sort()}
              selectedOptions={lsp}
              onSelect={(val) => {
                lsp.includes(val) ? setLsp(lsp.filter(f => f !== val)) :
                  setLsp([...lsp, val])
                setPage(1)
              }} size={isBulkOrderEnabled ? 'sm' : 'md'} />
          </div>
        </div>
        <div className='hidden xl:block shrink-0'>
          {isRetail ? <div className='flex items-center gap-2'>
            {isBulkOrderEnabled ? <Button variant={'outline'} size={'lg'} loading={isDownloadingLabels} onClick={handleDownloadLabels}>
              <Download />
              Download Labels
            </Button> : null}
            {isBulkOrderEnabled ? <Link to='/orders/bulk_order'>
              <Button variant={'blue'} size={'lg'}>
                <LayoutList />
                Add Bulk Order
              </Button>
            </Link> : null}
            {showAddSlotOrder ? <AddSlotOrderDialog token={token} onSuccess={() => getOrders.refetch()} /> : null}
            <Link to='/orders/add_order'>
              <Button variant={'blue'} size={'lg'}>
                <Plus />
                New Order
              </Button>
            </Link>
          </div> : null}
        </div>
      </div>
      {view === 'list' ? <div className='absolute top-64 left-1 right-1 bottom-15 lg:top-32'>
        <div className='absolute top-0 left-0 right-0 bottom-10 lg:bottom-12 overflow-auto'>
          <DataTable
            accountId={accountId}
            columns={columns}
            data={filteredOrders.slice((page - 1) * recordsPerPage, page * recordsPerPage)}
            isFetching={false}
            unfulfillEnabled={unfulfil_enabled || /super_admin/.test(role)}
            isSuperAdmin={/super_admin/.test(role)}
            isRetail={isRetail}
            onSuccess={(id, updatedOrder) => {
              queryClient.setQueryData(['orders', date, token], (oldData: Order[] = []) => {
                const index = oldData.findIndex(e => e.orderId === id)
                if (index === -1) return oldData

                const existingOrder = oldData[index]
                const mergedOrder = {
                  ...existingOrder,
                  ...updatedOrder,
                  orderId: existingOrder.orderId || id
                }

              return [
                  ...oldData.slice(0, index),
                  mergedOrder,
                  ...oldData.slice(index + 1)
                ]
              })
              if (isShikhar) {
                getOrders.refetch()
              }
            }}
          />
        </div>
        <Pagination recordCount={filteredOrders.length} page={page} setPage={page => setPage(page)} recordsPerPage={recordsPerPage} />
      </div> : <MapView orders={filteredOrders} type={markerType} />}
    </div>
  )
}
